import { initializeApp } from 'firebase/app';
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signInWithCustomToken,
  signInWithEmailLink,
  isSignInWithEmailLink,
  sendPasswordResetEmail,
  confirmPasswordReset,
  applyActionCode,
  checkActionCode,
  signOut,
  onAuthStateChanged,
  onIdTokenChanged,
  GoogleAuthProvider,
  FacebookAuthProvider,
  OAuthProvider,
  SAMLAuthProvider,
  multiFactor,
  getMultiFactorResolver,
  TotpMultiFactorGenerator,
  PhoneMultiFactorGenerator,
  PhoneAuthProvider,
  RecaptchaVerifier,
  type User,
  type MultiFactorResolver,
  type MultiFactorError,
} from 'firebase/auth';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
};

// Must match scio1's app name so Firebase IndexedDB auth persists across dual-live switches.
// scio1: initializeApp(config, "___auth___") in authService.js
const FIREBASE_APP_NAME = '___auth___';

const app = initializeApp(firebaseConfig, FIREBASE_APP_NAME);
export const auth = getAuth(app);

// Skip reCAPTCHA challenge in local development only (staging/preview should still verify)
if (import.meta.env.VITE_DISABLE_RECAPTCHA === 'true') {
  auth.settings.appVerificationDisabledForTesting = true;
}

// Providers
export const googleProvider = new GoogleAuthProvider();
export const facebookProvider = new FacebookAuthProvider();
export const microsoftProvider = new OAuthProvider('microsoft.com');

// SAML provider factory
export function createSamlProvider(accountId: string) {
  return new SAMLAuthProvider(`saml.id_${accountId}`);
}

// Get fresh token with auto-refresh if expiring within 3 minutes
export async function getFreshToken(forceRefresh = false): Promise<{
  token: string;
  expires: string;
  uid: string;
}> {
  const user = auth.currentUser;
  if (!user) throw new Error('No authenticated user');

  const tokenResult = await user.getIdTokenResult(forceRefresh);
  const expires = tokenResult.expirationTime;

  const diff = (new Date(expires).getTime() - Date.now()) / 60000;
  if (diff < 3) {
    const refreshed = await user.getIdTokenResult(true);
    return {
      token: refreshed.token,
      expires: refreshed.expirationTime,
      uid: user.uid,
    };
  }

  return { token: tokenResult.token, expires, uid: user.uid };
}

export {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signInWithCustomToken,
  signInWithEmailLink,
  isSignInWithEmailLink,
  sendPasswordResetEmail,
  confirmPasswordReset,
  applyActionCode,
  checkActionCode,
  signOut,
  onAuthStateChanged,
  onIdTokenChanged,
  GoogleAuthProvider,
  FacebookAuthProvider,
  OAuthProvider,
  SAMLAuthProvider,
  multiFactor,
  getMultiFactorResolver,
  TotpMultiFactorGenerator,
  PhoneMultiFactorGenerator,
  PhoneAuthProvider,
  RecaptchaVerifier,
};

// Convenience wrapper: extracts token string for auth callbacks (Apollo, DDP, etc.)
export async function getTokenString(): Promise<string | null> {
  try {
    const result = await getFreshToken();
    return result.token;
  } catch {
    return null;
  }
}

export type { User, MultiFactorResolver, MultiFactorError };
